<template>
  <div
    class="form-container"
  >
    <div class="action-buttons-header action-buttons-header--edit-screen">
      <a
        href="#groups/new"
        class="btn btn-outline-primary"
        role="button"
        data-test="AddNewGroupButton"
      >
        {{ $localize('AddNewGroupButton') }}
      </a>
    </div>

    <h2
      class="page-title-heading"
      data-test="GroupTitleHeading"
    >
      {{ $localize('GroupsTitle') }}
      <span class="font-size-smaller">({{ totalCount }})</span>
    </h2>

    <p class="lead">
      {{ $localize(groupsTabInfoRowText) }}
    </p>

    <div class="row">
      <div class="col-sm-4">
        <GroupFilterArea
          v-model="selectedItemId"
          :groups="allGroups"
          @selected-item-id-updated="updateSelectedItemId"
        />
      </div>

      <div class="col-sm-8 no-md-x-padding">
        <div v-if="isItemSelected && !loadingPage">
          <GroupDetails
            :selected-item-id="selectedItemId"
            :page-mode="constants.pageMode.edit"
            :validation-rules="validationRules"
            :all-groups="allGroups"
            @update-item="updateItem"
            @delete-item="deleteItem"
          />
        </div>

        <div
          v-else-if="!loadingPage"
          class="blank-area blank-area-height-admin-groups"
        >
          <i
            class="fa-solid fa-users"
            aria-hidden="true"
          />
          <h4 v-if="allGroups.length !== 0">
            {{ $localize('GroupsTabBlankAreaInfo') }}
          </h4>
          <h4 v-if="allGroups.length === 0">
            {{ $localize('GroupsTabNoUsersAreaInfo') }}
          </h4>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
  import { ref, computed } from 'vue';
  import GroupFilterArea from '@/VueComponents/GroupFilterArea/GroupFilterArea.vue';
  import GroupDetails from '@/VueComponents/Groups/GroupDetails.vue';
  import { GroupMemberDto } from '@/Models/groupManagement/groupMemberDto';
  import { GroupDetailsDto } from '@/Models/groupManagement/groupDetailsDto';
  import AccountSpecificLabelsProvider from '@/Utils/accountSpecificLabelsProvider';
  import { GroupSummaryDto } from '@/Models/groupManagement/groupSummaryDto';
  import constants from '@/constants';
  import { GroupDto } from '@/Models/groupManagement/groupDto';
  import usePageLoading from '@/VueCore/utils/usePageLoading';
  import validationRulesService from '@/Services/validationRulesService';
  import { useContextDataStore } from '@/VueCore/stores/contextDataStore';
  import GroupsService from '@/Services/groupsService';

  const allGroups = ref<GroupSummaryDto[]>([]);
  const validationRules = ref(null);

  const { loadingPage } = usePageLoading(async params => {
    const contextData = useContextDataStore();
    allGroups.value = await GroupsService.getAllGroups(contextData.userData.business.businessId);
    validationRules.value = await validationRulesService.getValidationRules('group');
  }, error => console.error('Failed to fetch validation rules:', error));

  const selectedItemId = ref('');

  const totalCount = computed(() => allGroups.value.length);
  const isItemSelected = computed(() => selectedItemId.value !== '');

  const groupsTabInfoRowText = AccountSpecificLabelsProvider.getAccountLabelKey('GroupsTabInfoRowText');

  const updateSelectedItemId = (newValue: string) => {
    selectedItemId.value = newValue;
  };

  const updateItem = (itemSummaryDto: GroupDto) => {
    const items = allGroups.value as GroupSummaryDto[];
    const updateIndex = items.findIndex(x => x.groupId === itemSummaryDto.groupId);
    const itemToUpdate = items[updateIndex];
    const newGroupMembers = itemSummaryDto.assignedGroupMembers
      .map(x => new GroupMemberDto(x.memberPersona.personaId, x.memberPersona.personaDisplayName, x.isGroupPrimaryContact));
    itemToUpdate.groupMembers = newGroupMembers;

    const newGroupDetails = createNewGroupDetails(itemSummaryDto);
    itemToUpdate.groupDetails = newGroupDetails;

    allGroups.value.splice(updateIndex, 1, itemToUpdate);
    allGroups.value = items;
  };

  const deleteItem = (itemId: string) => {
    const deleteIndex = allGroups.value.findIndex(x => x.groupId === itemId);
    if (deleteIndex !== -1) {
      allGroups.value.splice(deleteIndex, 1);
      selectedItemId.value = '';
    }
  };

  const createNewGroupDetails = (itemSummaryDto: GroupDto) => {
    return new GroupDetailsDto(
      itemSummaryDto.groupDetails.groupName,
      itemSummaryDto.groupDetails.address,
      itemSummaryDto.groupDetails.isLocationAssigned,
      itemSummaryDto.groupDetails.coordinate
    );
  };
</script>